import { useState, FormEvent } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Salvar dados no Supabase
      const { error } = await supabase
        .from('protocolo_leads')
        .insert([
          {
            name: formData.name,
            email: formData.email
          }
        ]);

      if (error) {
        console.error('Erro ao salvar no Supabase:', error);
      }

      // Redirecionar para o download do PDF imediatamente
      window.location.href = 'https://drive.google.com/file/d/1Q4ZIE_9kizIUMMkvvgTnI5kJPT3o5GNh/view?usp=sharing';
    } catch (error) {
      console.error('Erro:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full text-center">
          {/* Logo */}
          <div className="mb-12">
            <h1 className="text-5xl md:text-6xl font-bold tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              HOMEM 2.0
            </h1>
            <p className="text-[#D4AF37] text-lg tracking-wide">Reconstrução Pós-Divórcio</p>
          </div>

          {/* Headline */}
          <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            Você acordava às 3 da manhã pensando nela?
          </h2>

          {/* Subheadline */}
          <p className="text-[#D4AF37] text-xl md:text-2xl font-semibold mb-12">
            Baixe o Protocolo de Reconstrução de 90 Dias. Gratuito.
          </p>

          {/* Form */}
          <form 
            id="protocolo-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-[#141414] border border-[#D4AF37]/20 rounded-lg p-8 md:p-10 mb-8"
          >
            <div className="space-y-5 mb-8">
              <input
                type="text"
                name="name"
                placeholder="Seu Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-md px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors text-base"
              />
              <input
                type="email"
                name="email"
                placeholder="Seu Melhor E-mail"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-md px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors text-base"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] hover:bg-[#C19B2E] text-black font-bold py-5 px-8 rounded-md transition-all duration-300 text-lg tracking-wide whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isSubmitting ? 'PROCESSANDO...' : 'ACESSAR PROTOCOLO AGORA'}
            </button>
          </form>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
            <i className="ri-lock-line text-[#D4AF37]"></i>
            <span>Seus dados estão seguros e protegidos</span>
          </div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-20 px-6 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-16">
            O que você vai <span className="text-[#D4AF37]">encontrar:</span>
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Item 1 */}
            <div className="bg-[#141414] border border-[#D4AF37]/20 rounded-lg p-8 text-center hover:border-[#D4AF37]/40 transition-all duration-300">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-check-line text-3xl text-[#D4AF37]"></i>
              </div>
              <p className="text-lg leading-relaxed">
                <strong className="text-[#D4AF37]">O plano exato</strong> para parar de sofrer em 30 dias.
              </p>
            </div>

            {/* Item 2 */}
            <div className="bg-[#141414] border border-[#D4AF37]/20 rounded-lg p-8 text-center hover:border-[#D4AF37]/40 transition-all duration-300">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-[#D4AF37]"></i>
              </div>
              <p className="text-lg leading-relaxed">
                Como <strong className="text-[#D4AF37]">blindar sua mente</strong> contra recaídas.
              </p>
            </div>

            {/* Item 3 */}
            <div className="bg-[#141414] border border-[#D4AF37]/20 rounded-lg p-8 text-center hover:border-[#D4AF37]/40 transition-all duration-300">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-flashlight-line text-3xl text-[#D4AF37]"></i>
              </div>
              <p className="text-lg leading-relaxed">
                A <strong className="text-[#D4AF37]">rotina diária</strong> para reconstruir sua testosterona e confiança.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#0A0A0A] border-t border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            Copyright © 2025 Homem 2.0 · Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
